import { AfterViewChecked, Directive, ElementRef, forwardRef, HostBinding, inject, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yRole } from '../interfaces/a11y.interfaces';
import { normalizeAriaValue } from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

@Directive({
  selector: '[khA11yProgressbar]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yProgressbarDirective) }]
})
export class A11yProgressbarDirective extends A11yLabelDirective implements A11yRole, AfterViewChecked {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _translationService = inject(TranslationService);
  private _defaultLabel = 'Progress';

  @HostBinding('attr.role') public get role(): string | null {
    return this.isBootstrapProgressbarHost() ? null : 'progressbar';
  }

  public constructor() {
    super();
    this._translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._defaultLabel = this._translationService.instant('A11Y_PROGRESS_BAR.PROGRESSBAR') || this._defaultLabel;
      });
  }

  protected override get ariaLabelValue(): string | null {
    return normalizeAriaValue(this.a11yLabel) ?? this._defaultLabel;
  }

  public ngAfterViewChecked(): void {
    this.syncInternalProgressbars();
  }

  private syncInternalProgressbars(): void {
    if (!this.isBootstrapProgressbarHost()) {
      return;
    }

    const internalProgressbars = this._elementRef.nativeElement.querySelectorAll<HTMLElement>('[role="progressbar"]');

    internalProgressbars.forEach((progressbar) => {
      this.syncAttribute(progressbar, 'aria-label', this.ariaLabel);
      this.syncAttribute(progressbar, 'aria-labelledby', this.ariaLabelledBy);
      this.syncAttribute(progressbar, 'aria-describedby', this.ariaDescribedBy);
    });
  }

  private syncAttribute(element: HTMLElement, attributeName: string, value: string | null): void {
    if (value) {
      this._renderer.setAttribute(element, attributeName, value);
      return;
    }

    this._renderer.removeAttribute(element, attributeName);
  }

  private isBootstrapProgressbarHost(): boolean {
    return this._elementRef.nativeElement.tagName.toLowerCase() === 'progressbar';
  }
}
